/**
 * User Settings Page - Client Users
 * Security policy display, MFA setup, and password management
 */

import React, { useState, useEffect } from 'react';
import { User, Lock, Save, Loader2, AlertCircle, CheckCircle, AlertTriangle, Key, Plus, Trash2, Copy, Clock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import ConfirmDialog, { useConfirm } from '../components/ConfirmDialog';
import SecurityPolicyCard from '../components/SecurityPolicyCard';
// MfaSetupModal import removed - MFA coming soon
import ApiKeyModal from '../components/ApiKeyModal';
import PasswordStrengthIndicator from '../components/PasswordStrengthIndicator';
import { validateEmail, validatePassword, validateName } from '../utils/validation';
import { updateUserProfile, changePassword, getEnforcedPolicies } from '../services/userService';
// disableMfa import removed - MFA coming soon
import { listApiKeys, createApiKey, revokeApiKey } from '../services/apiKeyService';
import { getErrorMessage } from '../services/apiService';
import { setUserData, SecurityPolicies } from '../utils/tokenManager';

const UserSettings: React.FC = () => {
  const { user, updateUser } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { confirm, dialogProps: confirmDialogProps } = useConfirm();

  const [policies, setPolicies] = useState<SecurityPolicies | null>(user?.enforcedPolicies || null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Profile form
  const [profileData, setProfileData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
  });
  const [profileErrors, setProfileErrors] = useState<Record<string, string>>({});
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  // Password form
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({});
  const [isSavingPassword, setIsSavingPassword] = useState(false);

  // API Keys
  const [apiKeys, setApiKeys] = useState<any[]>([]);
  const [isLoadingApiKeys, setIsLoadingApiKeys] = useState(false);
  const [isCreatingApiKey, setIsCreatingApiKey] = useState(false);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [newApiKeyData, setNewApiKeyData] = useState<{ apiKey: string; name: string } | null>(null);
  const [newApiKeyName, setNewApiKeyName] = useState('');

  // Body scroll lock when sidebar is open on mobile
  useEffect(() => {
    if (isSidebarOpen && window.innerWidth < 1024) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isSidebarOpen]);

  useEffect(() => {
    loadPolicies();
    loadApiKeys();
  }, []);

  const loadPolicies = async () => {
    try {
      const fetchedPolicies = await getEnforcedPolicies();
      setPolicies(fetchedPolicies);
    } catch (err) {
      console.error('Failed to load policies:', err);
    }
  };

  const loadApiKeys = async () => {
    setIsLoadingApiKeys(true);
    try {
      const keys = await listApiKeys();
      setApiKeys(keys);
    } catch (err) {
      console.error('Failed to load API keys:', err);
      setError(getErrorMessage(err));
    } finally {
      setIsLoadingApiKeys(false);
    }
  };

  const handleCreateApiKey = async () => {
    if (!newApiKeyName.trim()) {
      setError('Please enter a name for your API key');
      return;
    }

    setIsCreatingApiKey(true);
    setError('');
    try {
      const response = await createApiKey({ name: newApiKeyName.trim() });
      setNewApiKeyData({
        apiKey: response.apiKey,
        name: newApiKeyName.trim(),
      });
      setShowApiKeyModal(true);
      setNewApiKeyName('');
      await loadApiKeys();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsCreatingApiKey(false);
    }
  };

  const handleRevokeApiKey = (keyId: string) => {
    confirm({
      title: 'Revoke this API key?',
      message: 'Any integration still using it stops working immediately. This cannot be undone — a replacement key has to be issued.',
      confirmLabel: 'Revoke key',
      tone: 'danger',
      action: () => doRevokeApiKey(keyId),
    });
  };

  const doRevokeApiKey = async (keyId: string) => {
    try {
      await revokeApiKey(keyId);
      setSuccess('API key revoked successfully');
      await loadApiKeys();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleCopyApiKey = (keyPreview: string) => {
    setSuccess(`API key preview copied: ${keyPreview}`);
    setTimeout(() => setSuccess(''), 2000);
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setProfileErrors({});

    const errors: Record<string, string> = {};
    const firstNameVal = validateName(profileData.firstName, 'First name');
    if (!firstNameVal.isValid) errors.firstName = firstNameVal.error!;

    const lastNameVal = validateName(profileData.lastName, 'Last name');
    if (!lastNameVal.isValid) errors.lastName = lastNameVal.error!;

    const emailVal = validateEmail(profileData.email);
    if (!emailVal.isValid) errors.email = emailVal.error!;

    if (Object.keys(errors).length > 0) {
      setProfileErrors(errors);
      return;
    }

    setIsSavingProfile(true);
    try {
      const updatedUser = await updateUserProfile(profileData);
      setUserData(updatedUser);
      updateUser(updatedUser);
      setSuccess('Profile updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setPasswordErrors({});

    const errors: Record<string, string> = {};
    if (!passwordData.currentPassword) errors.currentPassword = 'Current password is required';

    const newPasswordVal = validatePassword(passwordData.newPassword);
    if (!newPasswordVal.isValid) errors.newPassword = newPasswordVal.error!;

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    if (Object.keys(errors).length > 0) {
      setPasswordErrors(errors);
      return;
    }

    setIsSavingPassword(true);
    try {
      await changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
        confirmPassword: passwordData.confirmPassword,
      });
      setSuccess('Password changed successfully!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSavingPassword(false);
    }
  };

  const getPasswordExpiryWarning = () => {
    if (!user?.passwordExpiresAt) return null;

    const daysUntilExpiry = Math.floor(
      (new Date(user.passwordExpiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
    );

    if (user.passwordExpired) {
      return {
        message: 'Your password has expired. Please change it immediately.',
        severity: 'error' as const,
      };
    }

    if (daysUntilExpiry <= 7) {
      return {
        message: `Your password will expire in ${daysUntilExpiry} day${daysUntilExpiry !== 1 ? 's' : ''}. Please change it soon.`,
        severity: 'warning' as const,
      };
    }

    return null;
  };

  const passwordWarning = getPasswordExpiryWarning();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f8fafc] to-[#e2e8f0] text-[#0f172a]">
      {/* Ambient Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-radial from-[#c41e3a]/[0.03] to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-radial from-[#c41e3a]/[0.04] to-transparent rounded-full blur-3xl"></div>
      </div>

      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      <div className="flex-1 flex flex-col">
        <Navigation onMenuClick={() => setIsSidebarOpen(true)} />
        <main className="flex-1 overflow-auto pt-28 md:pt-32 lg:ml-[220px]">
          <div className="max-w-7xl mx-auto px-6 py-8">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-[#0f172a]">User Settings</h1>
              <p className="text-[#64748b] mt-1">Manage your security settings and profile</p>
            </div>

            <div className="space-y-6">
          {/* Global Messages */}
          {error && (
            <div className="p-4 rounded-xl border border-[#dc2626]/30 flex items-start space-x-3 bg-[#dc2626]/10">
              <AlertCircle className="w-5 h-5 text-[#dc2626] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-[#dc2626]">{error}</p>
            </div>
          )}

          {success && (
            <div className="p-4 rounded-xl border border-[#16a34a]/30 flex items-center space-x-3 bg-[#16a34a]/10">
              <CheckCircle className="w-5 h-5 text-[#16a34a]" />
              <p className="text-sm text-[#16a34a]">{success}</p>
            </div>
          )}

          {/* Password Expiry Warning */}
          {passwordWarning && (
            <div
              className={`p-4 rounded-xl border flex items-start space-x-3 ${
                passwordWarning.severity === 'error'
                  ? 'bg-[#dc2626]/10 border-[#dc2626]/30'
                  : 'bg-[#d97706]/10 border-[#d97706]/30'
              }`}
            >
              <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${passwordWarning.severity === 'error' ? 'text-[#dc2626]' : 'text-[#d97706]'}`} />
              <p className={`text-sm ${passwordWarning.severity === 'error' ? 'text-[#dc2626]' : 'text-[#d97706]'}`}>
                {passwordWarning.message}
              </p>
            </div>
          )}

          {/* Security Policies */}
          {policies && <SecurityPolicyCard policies={policies} />}

          {/* Profile Information */}
          <div className="glass-card rounded-xl p-6 border border-black/[0.08]">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-[#c41e3a]/10">
                <User className="w-6 h-6 text-[#c41e3a]" />
              </div>
              <h2 className="text-xl font-semibold text-[#0f172a]">Profile Information</h2>
            </div>

            <form onSubmit={handleProfileSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#475569] mb-2">First Name</label>
                  <input
                    type="text"
                    value={profileData.firstName}
                    onChange={(e) => {
                      setProfileData({ ...profileData, firstName: e.target.value });
                      setProfileErrors({ ...profileErrors, firstName: '' });
                    }}
                    className={`w-full px-4 py-3 rounded-xl border bg-white/80 text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#c41e3a] ${
                      profileErrors.firstName ? 'border-[#dc2626]' : 'border-black/[0.08]'
                    }`}
                  />
                  {profileErrors.firstName && <p className="mt-2 text-sm text-[#dc2626]">{profileErrors.firstName}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#475569] mb-2">Last Name</label>
                  <input
                    type="text"
                    value={profileData.lastName}
                    onChange={(e) => {
                      setProfileData({ ...profileData, lastName: e.target.value });
                      setProfileErrors({ ...profileErrors, lastName: '' });
                    }}
                    className={`w-full px-4 py-3 rounded-xl border bg-white/80 text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#c41e3a] ${
                      profileErrors.lastName ? 'border-[#dc2626]' : 'border-black/[0.08]'
                    }`}
                  />
                  {profileErrors.lastName && <p className="mt-2 text-sm text-[#dc2626]">{profileErrors.lastName}</p>}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#475569] mb-2">Email Address</label>
                <input
                  type="email"
                  value={profileData.email}
                  onChange={(e) => {
                    setProfileData({ ...profileData, email: e.target.value });
                    setProfileErrors({ ...profileErrors, email: '' });
                  }}
                  className={`w-full px-4 py-3 rounded-xl border bg-white/80 text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#c41e3a] ${
                    profileErrors.email ? 'border-[#dc2626]' : 'border-black/[0.08]'
                  }`}
                />
                {profileErrors.email && <p className="mt-2 text-sm text-[#dc2626]">{profileErrors.email}</p>}
              </div>

              <button
                type="submit"
                disabled={isSavingProfile}
                className="btn-primary disabled:opacity-50"
              >
                {isSavingProfile ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    <span>Save Changes</span>
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Password Management */}
          <div className="glass-card rounded-xl p-6 border border-black/[0.08]">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-[#c41e3a]/10">
                <Lock className="w-6 h-6 text-[#c41e3a]" />
              </div>
              <h2 className="text-xl font-semibold text-[#0f172a]">Change Password</h2>
            </div>

            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#475569] mb-2">Current Password</label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => {
                    setPasswordData({ ...passwordData, currentPassword: e.target.value });
                    setPasswordErrors({ ...passwordErrors, currentPassword: '' });
                  }}
                  className={`w-full px-4 py-3 rounded-xl border bg-white/80 text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#c41e3a] ${
                    passwordErrors.currentPassword ? 'border-[#dc2626]' : 'border-black/[0.08]'
                  }`}
                />
                {passwordErrors.currentPassword && <p className="mt-2 text-sm text-[#dc2626]">{passwordErrors.currentPassword}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-[#475569] mb-2">New Password</label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => {
                    setPasswordData({ ...passwordData, newPassword: e.target.value });
                    setPasswordErrors({ ...passwordErrors, newPassword: '' });
                  }}
                  className={`w-full px-4 py-3 rounded-xl border bg-white/80 text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#c41e3a] ${
                    passwordErrors.newPassword ? 'border-[#dc2626]' : 'border-black/[0.08]'
                  }`}
                />
                {passwordErrors.newPassword && <p className="mt-2 text-sm text-[#dc2626]">{passwordErrors.newPassword}</p>}
                <PasswordStrengthIndicator password={passwordData.newPassword} />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#475569] mb-2">Confirm New Password</label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => {
                    setPasswordData({ ...passwordData, confirmPassword: e.target.value });
                    setPasswordErrors({ ...passwordErrors, confirmPassword: '' });
                  }}
                  className={`w-full px-4 py-3 rounded-xl border bg-white/80 text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#c41e3a] ${
                    passwordErrors.confirmPassword ? 'border-[#dc2626]' : 'border-black/[0.08]'
                  }`}
                />
                {passwordErrors.confirmPassword && <p className="mt-2 text-sm text-[#dc2626]">{passwordErrors.confirmPassword}</p>}
              </div>

              <button
                type="submit"
                disabled={isSavingPassword}
                className="btn-primary disabled:opacity-50"
              >
                {isSavingPassword ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Changing...</span>
                  </>
                ) : (
                  <>
                    <Lock className="w-5 h-5" />
                    <span>Change Password</span>
                  </>
                )}
              </button>
            </form>
          </div>

          {/* API Key Management - Admin Only */}
          {user?.role === 'ClientAdmin' && <div className="glass-card rounded-xl p-6 border border-black/[0.08]">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-[#c41e3a]/10">
                  <Key className="w-6 h-6 text-[#c41e3a]" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-[#0f172a]">API Keys</h2>
                  <p className="text-sm text-[#64748b]">Manage your personal API keys for programmatic access</p>
                </div>
              </div>
            </div>

            {/* Create New API Key */}
            <div className="mb-6 p-4 rounded-xl bg-white/40 border border-black/[0.08]">
              <h3 className="text-sm font-medium text-[#475569] mb-3">Create New API Key</h3>
              <div className="flex space-x-3">
                <input
                  type="text"
                  value={newApiKeyName}
                  onChange={(e) => setNewApiKeyName(e.target.value)}
                  placeholder="API Key Name (e.g., Production Server)"
                  className="flex-1 px-4 py-2 rounded-lg border border-black/[0.08] bg-white/80 text-[#0f172a] placeholder-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-[#c41e3a]"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleCreateApiKey();
                    }
                  }}
                />
                <button
                  onClick={handleCreateApiKey}
                  disabled={isCreatingApiKey || !newApiKeyName.trim()}
                  className="btn-primary disabled:opacity-50"
                >
                  {isCreatingApiKey ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Creating...</span>
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      <span>Create</span>
                    </>
                  )}
                </button>
              </div>
              <p className="mt-2 text-xs text-[#64748b]">
                API keys allow you to authenticate API requests without using your password.
              </p>
            </div>

            {/* API Keys List */}
            {isLoadingApiKeys ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-[#c41e3a] animate-spin" />
              </div>
            ) : apiKeys.length === 0 ? (
              <div className="text-center py-8">
                <Key className="w-12 h-12 text-[#94a3b8] mx-auto mb-3" />
                <p className="text-[#64748b] text-sm">No API keys yet</p>
                <p className="text-[#94a3b8] text-xs mt-1">Create your first API key to get started</p>
              </div>
            ) : (
              <div className="space-y-3">
                {apiKeys.map((key) => (
                  <div
                    key={key.id}
                    className="p-4 rounded-xl bg-white/40 border border-black/[0.08] hover:border-black/[0.12] transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h3 className="font-medium text-[#0f172a]">{key.name}</h3>
                          {key.isActive ? (
                            <span className="px-2 py-0.5 text-xs rounded bg-[#16a34a]/10 text-[#16a34a] border border-[#16a34a]/20">
                              Active
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 text-xs rounded bg-[#dc2626]/10 text-[#dc2626] border border-[#dc2626]/20">
                              Revoked
                            </span>
                          )}
                        </div>
                        <div className="flex items-center space-x-2 mb-2">
                          <code className="text-xs font-mono text-[#475569] bg-white/60 px-2 py-1 rounded">
                            {key.keyPreview}
                          </code>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(key.keyPreview);
                              handleCopyApiKey(key.keyPreview);
                            }}
                            className="p-1 hover:bg-black/[0.08] rounded transition-colors"
                            title="Copy key preview"
                          >
                            <Copy className="w-3 h-3 text-[#64748b]" />
                          </button>
                        </div>
                        <div className="flex items-center space-x-4 text-xs text-[#64748b]">
                          <div className="flex items-center space-x-1">
                            <Clock className="w-3 h-3" />
                            <span>Created {new Date(key.createdAt).toLocaleDateString()}</span>
                          </div>
                          {key.lastUsedAt && (
                            <div className="flex items-center space-x-1">
                              <span>Last used {new Date(key.lastUsedAt).toLocaleDateString()}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      {key.isActive && (
                        <button
                          onClick={() => handleRevokeApiKey(key.id)}
                          className="ml-4 p-2 hover:bg-[#dc2626]/10 rounded-lg transition-colors text-[#dc2626]"
                          title="Revoke API key"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-4 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <p className="text-xs text-blue-700">
                <strong>Important:</strong> API keys are shown in full only once during creation.
                Store them securely - you won't be able to see the full key again.
              </p>
            </div>
          </div>}

            </div>
          </div>
        </main>
        <Footer />
      </div>

      {/* API Key Display Modal */}
      {newApiKeyData && (
        <ApiKeyModal
          isOpen={showApiKeyModal}
          onClose={() => {
            setShowApiKeyModal(false);
            setNewApiKeyData(null);
          }}
          apiKey={newApiKeyData.apiKey}
          keyName={newApiKeyData.name}
        />
      )}

      <ConfirmDialog {...confirmDialogProps} />
    </div>
  );
};

export default UserSettings;
